import { DNode } from '@dojo/framework/core/interfaces';
import { create, v, w } from '@dojo/framework/core/vdom';
import { theme } from '@dojo/framework/core/middleware/theme';

import { pathmap, registry, keymap, createNlsMiddleware, userstate } from 'lcc-state';
import { TopicDetails, ParentTree } from 'lcc-state/interfaces';

import { TopicProperties } from '@holmescorp/book-widgets/interfaces';

import { Layout, LearnerTools, ContentContainer, Breadcrumbs, MicroProgress } from './index';
import GroupNav from './panels/group-nav';
import MicroToolsButton from './tools-button';

import css from '../css/topics.m.css';

export interface TopicSingleProperties extends TopicProperties {
	parent: TopicDetails;
}

const factory = create({
	theme,
	nls: createNlsMiddleware('ditabook'),
	userstate
}).properties<TopicSingleProperties>();

export const TopicSingle = factory(({ id: instanceId, properties, middleware: { theme, nls, userstate } }): DNode => {
	const { topic, parent: topicset } = properties();
	const {
		messages: {
			micro_tgf_quiz_label = 'Take Topic Group Family Quiz',
			micro_tgf_flashcards_label = 'Module Flashcards'
		}
	} = nls();

	const themeCss = theme.classes(css);
	const status: ParentTree = userstate.get(userstate.path('status'));

	const id = 'topic-single-wrapper';
	const widgetId = `${id}-${instanceId}`;

	const topicGroups = topicset.children?.filter((tg) => !('shortdesc' in tg)) || [];
	const total = topicGroups.length;
	const complete = topicGroups
		.map((tg) => ({
			attributes: tg.topicgroup.attributes,
			progress: status[tg.topicgroup.attributes?.key || ''].data
		}))
		.filter((tg) => tg.progress.complete === tg.progress.total).length;
	const label = v('span', {}, [`Reading Progress`]);

	const tgfKey = topicset.attributes?.key || '';
	const currentTopicGroupKey = topic.attributes?.key;

	return v('div', { id: `${id}-main` }, [
		v(
			'div',
			{
				id,
				widgetId,
				key: widgetId,
				xqa: `${id}-layout`
			},
			[
				w(
					Layout,
					{
						xqa: `${id}-layout-widget`,
						id: `${id}-layout-widget-${instanceId}`,
						key: `${id}-two-column-layout`
					},
					[
						{
							leading: [
								w(ContentContainer, {}, [
									v('div', { id: `${id}-title`, classes: [themeCss.header] }, [
										w(Breadcrumbs, {}),
										w(ContentContainer, { additionalClasses: [themeCss.progressContainer] }, [
											w(MicroProgress, { max: total, value: complete, label })
										])
									]),
									w('TopicSingle', { ...topic.attributes })
								])
							],
							trailing: [
								w(LearnerTools, { includeQuizButton: false }),
								w(GroupNav, { currentTopicGroupKey, topicset }),
								v('div', { classes: [themeCss.actionButton] }, [
									w(MicroToolsButton, {
										labelText: micro_tgf_flashcards_label,
										onClick: () => {
											const { path } = keymap.get(tgfKey)!;
											const { view } = pathmap.get(`${path}/flashcards`)!;
											view();
										},
										onDown: () => {
											const { path } = keymap.get(tgfKey)!;
											const { view } = pathmap.get(`${path}/flashcards`)!;
											view();
										}
									})
								]),
								v('div', { classes: [themeCss.actionButton] }, [
									w(MicroToolsButton, {
										labelText: micro_tgf_quiz_label,
										onClick: () => {
											const { path } = keymap.get(tgfKey)!;
											const { view } = pathmap.get(`${path}/quiz`)!;
											view();
										},
										onDown: () => {
											const { path } = keymap.get(tgfKey)!;
											const { view } = pathmap.get(`${path}/quiz`)!;
											view();
										}
									})
								])
							]
						}
					]
				)
			]
		)
	]);
});

registry.define('MicroTopicSingle', TopicSingle);

export default TopicSingle;
